import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Mic, MicOff, Volume2, VolumeX, Loader2, Sparkles, ChevronDown, ArrowRight, User, Bot, Trash2, History, AudioWaveform, Plus, Camera, Image, FileText, Briefcase, Wand2, Calendar, CheckSquare, Lightbulb, Users, Mail, UserPlus, Lock, Zap, Bell, ShoppingCart, FileEdit, Calculator, Share2, Brain, Headphones, TrendingUp, Video } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import RegisterPromptModal from '../RegisterPromptModal';
import SocialPostingFlow from './SocialPostingFlow';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Génère un session ID persistant
const getSessionId = () => {
  const stored = localStorage.getItem('ai_session_id');
  if (stored) return stored;
  const newId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem('ai_session_id', newId);
  return newId;
};

// ═══════════════════════════════════════════════════════════════════════════════
// MODAL SUPERPOUVOIRS WAITLIST
// ═══════════════════════════════════════════════════════════════════════════════
const SuperpouvoirsWaitlistModal = ({ isOpen, onClose, feature, onSubscribe }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  
  const handleSubmit = async () => {
    if (!email && !localStorage.getItem('userId')) return;
    
    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem('userId');
      
      if (userId) {
        // Utilisateur connecté → waitlist superpouvoirs
        const response = await fetch(`${API_URL}/api/ai/waitlist/superpouvoirs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, feature: feature.label })
        });
        if (response.ok) {
          setIsSubscribed(true);
          onSubscribe && onSubscribe(feature);
        }
      } else if (email) {
        // Visiteur → collecte email prospect
        const response = await fetch(`${API_URL}/api/ai/waitlist/prospect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            email, 
            interest: 'superpouvoirs',
            user_type: 'particulier'
          })
        });
        if (response.ok) {
          setIsSubscribed(true);
          onSubscribe && onSubscribe(feature);
        }
      }
    } catch (error) {
      console.error('Waitlist error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header avec gradient */}
          <div className="bg-gradient-to-r from-[#1E3A5F] to-[#3B82F6] p-6 text-white text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center">
              {feature?.icon ? <feature.icon className="w-8 h-8" /> : <Zap className="w-8 h-8" />}
            </div>
            <h3 className="text-xl font-bold mb-1">{feature?.label || 'Superpouvoir'}</h3>
            <p className="text-white/80 text-sm">Bientôt disponible</p>
          </div>
          
          {/* Contenu */}
          <div className="p-6">
            {isSubscribed ? (
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckSquare className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Tu es inscrit !</h4>
                <p className="text-gray-600 text-sm mb-4">
                  On te prévient dès que "{feature?.label}" est disponible.
                </p>
                <button
                  onClick={onClose}
                  className="w-full py-2.5 bg-[#1E3A5F] text-white rounded-lg font-medium hover:bg-[#2d5a8a] transition-colors"
                >
                  Super, merci !
                </button>
              </div>
            ) : (
              <>
                <p className="text-gray-600 text-sm mb-4 text-center">
                  {feature?.description || "Cette fonctionnalité fait partie des superpouvoirs de ton assistant. Inscris-toi pour être prévenu dès qu'elle est disponible !"}
                </p>
                
                {!localStorage.getItem('userId') && (
                  <div className="mb-4">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Ton email..."
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F]"
                    />
                  </div>
                )}
                
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || (!localStorage.getItem('userId') && !email)}
                  className="w-full py-2.5 bg-gradient-to-r from-[#F97316] to-[#ea6c0a] text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Bell className="w-4 h-4" />
                      Me prévenir
                    </>
                  )}
                </button>
                
                <button
                  onClick={onClose}
                  className="w-full mt-2 py-2 text-gray-500 text-sm hover:text-gray-700"
                >
                  Plus tard
                </button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const FloatingAIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(false);
  const [voiceOnlyMode, setVoiceOnlyMode] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [isProMode, setIsProMode] = useState(false);
  const [isHiddenByDrawer, setIsHiddenByDrawer] = useState(false);
  
  // État pour l'animation "Active tes superpouvoirs"
  const [isExpanded, setIsExpanded] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0); // 0: initial, 1: first expand, 2: closed, 3: second expand, 4: done
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [loginPopupAction, setLoginPopupAction] = useState('cette fonctionnalité');
  
  // État pour le modal Superpouvoirs
  const [showSuperpouvoirModal, setShowSuperpouvoirModal] = useState(false);
  const [selectedSuperpouvoir, setSelectedSuperpouvoir] = useState(null);
  const [showSuperpouvoirsSection, setShowSuperpouvoirsSection] = useState(false);
  
  // État pour le Social Posting Flow
  const [showSocialPostingFlow, setShowSocialPostingFlow] = useState(false);
  
  // État pour les notifications (badge + résumé)
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationSummary, setNotificationSummary] = useState(null);
  const [showModeProPopup, setShowModeProPopup] = useState(false);
  
  const location = useLocation();
  
  // Masquer en mode Réseau (routes /reseau/*), sur la page Assistant, ou dans les cockpits
  const isReseauMode = location.pathname.startsWith('/reseau');
  const isAssistantPage = location.pathname === '/assistant';
  const isCockpitMode = location.pathname.startsWith('/cockpit-pro') || location.pathname.startsWith('/cockpit-particulier');
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const navigate = useNavigate();

  // Animation séquentielle "Active tes superpouvoirs"
  // Séquence: 2s attente → expand 3s → close 5s → expand 3s → close définitif
  useEffect(() => {
    // Reset animation on page change
    setAnimationPhase(0);
    setIsExpanded(false);
    
    // Phase 1: Attendre 2 secondes puis ouvrir
    const timer1 = setTimeout(() => {
      if (!isOpen) {
        setIsExpanded(true);
        setAnimationPhase(1);
      }
    }, 2000);
    
    // Phase 2: Fermer après 3 secondes (total: 5s)
    const timer2 = setTimeout(() => {
      setIsExpanded(false);
      setAnimationPhase(2);
    }, 5000);
    
    // Phase 3: Rouvrir après 5 secondes de fermeture (total: 10s)
    const timer3 = setTimeout(() => {
      if (!isOpen) {
        setIsExpanded(true);
        setAnimationPhase(3);
      }
    }, 10000);
    
    // Phase 4: Fermer définitivement après 3 secondes (total: 13s)
    const timer4 = setTimeout(() => {
      setIsExpanded(false);
      setAnimationPhase(4);
    }, 13000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [location.pathname]); // Relancer à chaque changement de page

  // Fermer l'expansion quand on ouvre le chat
  useEffect(() => {
    if (isOpen) {
      setIsExpanded(false);
    }
  }, [isOpen]);

  // Écouter l'événement pour cacher/montrer le FAB quand un drawer est ouvert
  useEffect(() => {
    const handleDrawerState = (e) => {
      setIsHiddenByDrawer(e.detail.isOpen);
    };
    window.addEventListener('universDrawerStateChange', handleDrawerState);
    return () => window.removeEventListener('universDrawerStateChange', handleDrawerState);
  }, []);

  // Charge l'historique au premier ouverture
  const loadHistory = useCallback(async () => {
    if (historyLoaded) return;
    try {
      const response = await fetch(`${API_URL}/api/ai/session/${sessionId}/history?limit=50`);
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages);
        }
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setHistoryLoaded(true);
    }
  }, [sessionId, historyLoaded]);

  useEffect(() => {
    if (isOpen && !historyLoaded) {
      loadHistory();
    }
  }, [isOpen, historyLoaded, loadHistory]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Auto-focus désactivé sur mobile pour privilégier la note vocale
  useEffect(() => {
    if (isOpen && inputRef.current && !voiceOnlyMode) {
      const isMobile = window.innerWidth < 640;
      if (!isMobile) {
        inputRef.current.focus();
      }
    }
  }, [isOpen, voiceOnlyMode]);

  // Charger les notifications pour le badge et le résumé Abra
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    
    const fetchNotifications = async () => {
      try {
        // Récupérer le compteur pour le badge
        const countResponse = await fetch(`${API_URL}/api/notifications/unread-count/${userId}`);
        if (countResponse.ok) {
          const countData = await countResponse.json();
          setNotificationCount(countData.count || 0);
        }
        
        // Récupérer le résumé pour Abra (seulement quand le chat est ouvert)
        if (isOpen) {
          const summaryResponse = await fetch(`${API_URL}/api/notifications/summary/${userId}`);
          if (summaryResponse.ok) {
            const summaryData = await summaryResponse.json();
            setNotificationSummary(summaryData);
          }
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    
    fetchNotifications();
    
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [isOpen]);

  const handleAction = (action) => {
    if (!action) return;
    
    switch (action.type) {
      case 'navigate':
        navigate(action.route);
        setIsOpen(false);
        break;
      case 'show_artisans':
        break;
      case 'open_publish_modal':
        window.dispatchEvent(new CustomEvent('openPublishModal', { detail: action.data }));
        break;
      case 'prompt_login':
        navigate('/connexion');
        break;
      default:
        break;
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    
    const userMessage = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    
    try {
      // Récupérer l'userId depuis les différentes sources
      let userId = localStorage.getItem('userId');
      if (!userId) {
        const ecosystemUser = localStorage.getItem('ecosystem_user');
        const buyerUser = localStorage.getItem('buyer_user');
        const clientUser = localStorage.getItem('client_user');
        
        if (ecosystemUser) {
          try { userId = JSON.parse(ecosystemUser).id; } catch(e) {}
        }
        if (!userId && buyerUser) {
          try { userId = JSON.parse(buyerUser).id; } catch(e) {}
        }
        if (!userId && clientUser) {
          try { userId = JSON.parse(clientUser).id; } catch(e) {}
        }
      }
      
      const response = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          session_id: sessionId,
          user_id: userId || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const assistantMessage = { 
          role: 'assistant', 
          content: data.message, 
          timestamp: new Date().toISOString(),
          actions: data.actions
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        if (data.actions?.length > 0) {
          data.actions.forEach(action => handleAction(action));
        }
        
        // Auto-speak en mode voice-only ou si autoSpeak activé
        if ((voiceOnlyMode || autoSpeak) && data.message) {
          speakText(data.message);
        }
      } else if (response.status === 402) {
        // Crédits insuffisants
        const creditError = data.detail || {};
        const errorContent = `💳 **Crédits insuffisants**\n\nTu as besoin de ${creditError.required || 1} crédit(s), mais il t'en reste ${creditError.available || 0}.\n\nClique sur le badge 💰 en haut pour recharger tes crédits ou découvrir nos forfaits.`;
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: errorContent,
          timestamp: new Date().toISOString(),
          isError: true
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Désolé, une erreur est survenue. Réessaie !',
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      // Message d'erreur plus explicite pour le débogage
      let errorMessage = 'Oups, je n\'arrive pas à me connecter.';
      
      if (error.message && error.message.includes('Failed to fetch')) {
        errorMessage = 'Impossible de contacter le serveur. Vérifie ta connexion internet ou réessaie dans un instant.';
        console.error('API URL:', `${API_URL}/api/ai/chat`);
      } else if (error.message && error.message.includes('NetworkError')) {
        errorMessage = 'Erreur réseau. Le serveur est peut-être temporairement indisponible.';
      }
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: errorMessage,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Microphone access error:', error);
      alert('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'audio.webm');
      formData.append('language', 'fr');
      
      const response = await fetch(`${API_URL}/api/ai/transcribe`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok && data.text) {
        if (voiceOnlyMode) {
          // En mode voice-only, envoie directement
          await sendMessage(data.text);
        } else {
          setInputValue(data.text);
          await sendMessage(data.text);
        }
      }
    } catch (error) {
      console.error('Transcription error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const speakText = async (text) => {
    if (isSpeaking) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsSpeaking(false);
      return;
    }
    
    setIsSpeaking(true);
    try {
      const formData = new FormData();
      formData.append('text', text);
      formData.append('voice', 'nova');
      
      const response = await fetch(`${API_URL}/api/ai/speak`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsSpeaking(false);
        audioRef.current.play();
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsSpeaking(false);
    }
  };

  const clearHistory = async () => {
    try {
      // Supprimer la session côté serveur
      await fetch(`${API_URL}/api/ai/session/${sessionId}`, { method: 'DELETE' });
      
      // Vider les messages localement
      setMessages([]);
      
      // Générer un nouveau session ID et le sauvegarder
      const newId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('ai_session_id', newId);
      
      // Forcer le rechargement de l'historique vide
      setHistoryLoaded(false);
      
      console.log('✅ Historique supprimé, nouvelle session:', newId);
    } catch (error) {
      console.error('Clear history error:', error);
      // Même en cas d'erreur serveur, vider localement
      setMessages([]);
    }
  };

  const toggleProMode = () => {
    // Afficher le popup au lieu d'activer le mode
    setShowModeProPopup(true);
    setShowPlusMenu(false);
  };

  // Gestion des fichiers uploadés
  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    setShowPlusMenu(false);
    setIsLoading(true);
    
    // Afficher le message utilisateur avec aperçu
    const filePreview = type === 'image' || type === 'camera' 
      ? URL.createObjectURL(file) 
      : null;
    
    const userMessage = { 
      role: 'user', 
      content: `📎 ${type === 'camera' ? 'Photo prise' : type === 'image' ? 'Image envoyée' : 'Fichier envoyé'}: ${file.name}`,
      timestamp: new Date().toISOString(),
      attachment: {
        type: type,
        name: file.name,
        size: file.size,
        preview: filePreview
      }
    };
    setMessages(prev => [...prev, userMessage]);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId);
      formData.append('file_type', type);
      
      // Envoyer le user_id pour l'import CSV intelligent
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      if (userId) {
        formData.append('user_id', userId);
      }
      
      const response = await fetch(`${API_URL}/api/ai/upload`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Formatage spécial pour les imports CSV réussis
        let messageContent = data.message || `J'ai bien reçu ton ${type === 'file' ? 'fichier' : 'image'}. Comment puis-je t'aider avec ?`;
        
        // Ajouter les résultats d'import si présents
        if (data.import_result && data.import_result.imported_count > 0) {
          messageContent += `\n\n✨ Import réalisé avec succès !`;
        }
        
        const assistantMessage = { 
          role: 'assistant', 
          content: messageContent,
          timestamp: new Date().toISOString(),
          importResult: data.import_result || null
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Désolé, je n\'ai pas pu traiter ce fichier. Réessaie !',
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `J'ai bien reçu "${file.name}". Comment puis-je t'aider avec ce ${type === 'file' ? 'fichier' : 'contenu'} ?`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file, 'camera');
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file, 'image');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file, 'file');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  // Vérifie si l'utilisateur est connecté
  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    return !!(token && userId);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // FONCTIONNALITÉS GRATUITES (disponibles maintenant)
  // ═══════════════════════════════════════════════════════════════════════════════
  const personalActions = [
    { label: 'Inviter', icon: UserPlus, path: '/cockpit-particulier/cercles', color: '#E8650A' },
    { label: 'Messages', icon: MessageCircle, path: '/cockpit-particulier/messagerie', color: '#1E3A5F' },
    { label: 'Mail', icon: Mail, path: '/cockpit-particulier/emails', color: '#E8650A' },
    { label: 'Agenda', icon: Calendar, path: '/cockpit-particulier/agenda', color: '#1E3A5F' },
    { label: 'Tâches', icon: CheckSquare, path: '/cockpit-particulier/taches', color: '#E8650A' },
    { label: 'Notes', icon: FileText, path: '/cockpit-particulier/notes', color: '#1E3A5F' },
    { label: 'Idées', icon: Lightbulb, path: '/cockpit-particulier/idees', color: '#E8650A' },
    { label: 'Actu', icon: Users, path: '/cockpit-particulier/cercles/feed', color: '#1E3A5F' },
  ];

  // ═══════════════════════════════════════════════════════════════════════════════
  // SUPERPOUVOIRS (verrouillés - bientôt disponibles)
  // ═══════════════════════════════════════════════════════════════════════════════
  const superpouvoirs = [
    { 
      label: 'Agent Négociation', 
      icon: ShoppingCart, 
      color: '#8B5CF6',
      description: 'Ton assistant négocie tes annonces à ta place selon tes instructions. Tu définis ton prix minimum, il gère les discussions.'
    },
    { 
      label: 'Agent Messages', 
      icon: MessageCircle, 
      color: '#EC4899',
      description: 'Réponses automatiques selon des règles que tu définis. Tu interviens que quand c\'est important.'
    },
    { 
      label: 'Mails Zéro Effort', 
      icon: Mail, 
      color: '#10B981',
      description: 'Ton assistant analyse comment tu écris et répond comme toi. Tu valides en un clic.'
    },
    { 
      label: 'Transcription', 
      icon: Headphones, 
      color: '#F59E0B',
      description: 'Enregistre une réunion ou un appel, Ton assistant transcrit et te fait un résumé avec les actions à faire.'
    },
    { 
      label: 'Liste Courses IA', 
      icon: ShoppingCart, 
      color: '#6366F1',
      description: 'Dis "raclette pour 8 samedi" et ton assistant te génère la liste complète automatiquement.'
    },
    { 
      label: 'Budget', 
      icon: Calculator, 
      color: '#14B8A6',
      description: 'Suivi de tes dépenses par catégorie avec alertes quand tu dépasses ton budget.'
    },
    { 
      label: 'Post Réseau Social', 
      icon: Share2, 
      color: '#3B82F6',
      description: 'Envoie une image + dis le thème, Ton assistant crée le post optimisé et le publie directement sur le réseau social.'
    },
    { 
      label: 'Annonces Magiques', 
      icon: Camera, 
      color: '#F97316',
      description: 'Prends une photo, dis deux mots, Abra crée l\'annonce complète avec le bon prix.'
    },
    { 
      label: 'Mémoire Longue', 
      icon: Brain, 
      color: '#1E3A5F',
      description: 'Ton assistant retient tes préférences, projets, historique. Pas besoin de te répéter.'
    },
  ];

  // Superpouvoirs pour les Pros
  const superpouvoirs_pro = [
    { 
      label: 'Comptes-rendus Auto', 
      icon: FileEdit, 
      color: '#8B5CF6',
      description: 'Résumé automatique de l\'activité du jour : clients contactés, devis envoyés, CA réalisé.'
    },
    { 
      label: 'Transcription Appels', 
      icon: Headphones, 
      color: '#EC4899',
      description: 'Enregistre tes appels clients, Ton assistant transcrit et résume avec les actions à faire.'
    },
    { 
      label: 'Post Réseaux Sociaux', 
      icon: Share2, 
      color: '#10B981',
      description: 'Envoie une image + le thème, Abra formate l\'image, génère le texte optimisé pour ton activité et publie sur tous tes réseaux.'
    },
    { 
      label: 'Vendeur Virtuel', 
      icon: Bot, 
      color: '#F59E0B',
      description: 'Une IA qui répond aux clients sur ta boutique 24h/24, qualifie les prospects et te prévient.'
    },
    { 
      label: 'Réponses Auto Devis', 
      icon: FileText, 
      color: '#6366F1',
      description: 'Répond automatiquement aux demandes de devis selon tes règles et disponibilités.'
    },
    { 
      label: 'Import Photo → Fiche', 
      icon: Camera, 
      color: '#14B8A6',
      description: 'Prends une photo d\'un produit, Ton assistant crée la fiche complète avec description et prix suggéré.'
    },
    { 
      label: 'Stats Avancées', 
      icon: TrendingUp, 
      color: '#F97316',
      description: 'Tableaux de bord détaillés : CA, conversion, clients, prévisions.'
    },
    { 
      label: 'Aide Comptable', 
      icon: Calculator, 
      color: '#1E3A5F',
      description: 'Suivi comptable, exports pour ton comptable, préparation des déclarations.'
    },
  ];

  // Gère le clic sur un superpouvoir verrouillé
  const handleSuperpouvoirClick = (superpouvoir) => {
    setSelectedSuperpouvoir(superpouvoir);
    setShowSuperpouvoirModal(true);
  };

  // Gère le clic sur un bouton d'accès rapide
  const handlePersonalAction = (action) => {
    if (isAuthenticated()) {
      navigate(action.path);
      setIsOpen(false);
    } else {
      setLoginPopupAction(action.label.toLowerCase());
      setShowLoginPopup(true);
    }
  };

  const toggleVoiceMode = () => {
    setVoiceOnlyMode(!voiceOnlyMode);
    if (!voiceOnlyMode) {
      setAutoSpeak(true); // Active auto-speak en mode voice
    }
  };

  // Rendu mode Voice-Only (style Siri)
  const renderVoiceOnlyMode = () => (
    <motion.div
      data-testid="ai-assistant-voice-mode"
      className="fixed z-[9998] bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] shadow-2xl overflow-hidden
        max-sm:inset-0 max-sm:bottom-[60px] max-sm:rounded-none max-sm:h-[calc(100vh-60px)]
        sm:bottom-24 sm:right-6 sm:w-[300px] sm:rounded-3xl"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
    >
      {/* Header minimal */}
      <div className="flex items-center justify-between px-4 py-3">
        <button
          onClick={toggleVoiceMode}
          className="text-white/70 hover:text-white text-xs flex items-center gap-1"
        >
          <MessageCircle className="w-4 h-4" /> Mode chat
        </button>
        <button
          onClick={() => setIsOpen(false)}
          className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Zone principale */}
      <div className="px-6 py-8 flex flex-col items-center">
        {/* Indicateur d'état */}
        <div className="mb-6 text-center">
          {isLoading ? (
            <p className="text-white/80 text-sm">Je réfléchis...</p>
          ) : isSpeaking ? (
            <p className="text-white/80 text-sm">Je parle...</p>
          ) : isRecording ? (
            <p className="text-white text-sm font-medium animate-pulse">Je t'écoute...</p>
          ) : (
            <p className="text-white/60 text-sm">Appuie et parle</p>
          )}
        </div>

        {/* Bouton micro principal */}
        <motion.button
          onClick={isRecording ? stopRecording : startRecording}
          disabled={isLoading || isSpeaking}
          className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
            isRecording 
              ? 'bg-red-500 shadow-lg shadow-red-500/50' 
              : 'bg-white/20 hover:bg-white/30'
          } ${(isLoading || isSpeaking) ? 'opacity-50 cursor-not-allowed' : ''}`}
          whileTap={{ scale: 0.95 }}
          animate={isRecording ? { scale: [1, 1.1, 1] } : {}}
          transition={isRecording ? { repeat: Infinity, duration: 1.5 } : {}}
        >
          {isLoading ? (
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          ) : isSpeaking ? (
            <AudioWaveform className="w-10 h-10 text-white animate-pulse" />
          ) : isRecording ? (
            <MicOff className="w-10 h-10 text-white" />
          ) : (
            <Mic className="w-10 h-10 text-white" />
          )}
        </motion.button>

        {/* Dernier message */}
        {messages.length > 0 && (
          <motion.div 
            className="mt-6 text-center max-h-24 overflow-y-auto"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-white/90 text-sm leading-relaxed">
              {messages[messages.length - 1]?.content?.slice(0, 150)}
              {messages[messages.length - 1]?.content?.length > 150 ? '...' : ''}
            </p>
          </motion.div>
        )}

        {/* Bouton replay */}
        {messages.length > 0 && messages[messages.length - 1]?.role === 'assistant' && (
          <button
            onClick={() => speakText(messages[messages.length - 1].content)}
            className="mt-4 text-white/60 hover:text-white text-xs flex items-center gap-1"
          >
            <Volume2 className="w-3 h-3" /> Réécouter
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-black/20 flex justify-center">
        <button
          onClick={clearHistory}
          className="text-white/50 hover:text-white/80 text-xs flex items-center gap-1"
        >
          <Trash2 className="w-3 h-3" /> Nouvelle conversation
        </button>
      </div>
    </motion.div>
  );

  // Rendu mode Chat classique
  const renderChatMode = () => (
    <motion.div
      data-testid="ai-assistant-window"
      className="fixed z-[9998] bg-white shadow-2xl flex flex-col overflow-hidden border border-gray-200
        max-sm:inset-0 max-sm:bottom-[60px] max-sm:rounded-none max-sm:h-[calc(100vh-60px)]
        sm:bottom-24 sm:right-6 sm:w-[380px] sm:h-[520px] sm:max-h-[70vh] sm:rounded-2xl"
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.9 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm text-[#F97316]">Ton Assistant</h3>
            <p className="text-xs text-white/70">
              {historyLoaded && messages.length > 0 
                ? `${messages.length} messages` 
                : 'Ton super assistant'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleVoiceMode}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center"
            title="Mode vocal"
          >
            <AudioWaveform className="w-4 h-4" />
          </button>
          <button
            onClick={() => setAutoSpeak(!autoSpeak)}
            className={`w-8 h-8 rounded-full transition-colors flex items-center justify-center ${autoSpeak ? 'bg-white/30' : 'bg-white/10 hover:bg-white/20'}`}
            title={autoSpeak ? 'Désactiver la voix' : 'Activer la voix'}
          >
            {autoSpeak ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <button
            onClick={clearHistory}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center"
            title="Effacer l'historique"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {/* Indicateur historique chargé */}
        {historyLoaded && messages.length > 0 && (
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400 py-2">
            <History className="w-3 h-3" />
            <span>Historique restauré</span>
          </div>
        )}
        
        {messages.length === 0 ? (
          <div className="text-center py-4">
            <h4 className="font-semibold text-[#1E3A5F] text-lg mb-1">Salut ! Je suis ton super assistant</h4>
            <p className="text-xs text-gray-500 mb-4">
              Tu peux m'appeler comme tu le souhaites ✨
            </p>
            
            {/* Bandeau résumé notifications */}
            {notificationSummary && notificationSummary.has_notifications && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-gradient-to-r from-[#F97316]/10 to-[#F97316]/5 border border-[#F97316]/20 rounded-xl"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#F97316] flex items-center justify-center flex-shrink-0">
                    <Bell className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-800 mb-2">
                      {notificationSummary.summary_text}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {notificationSummary.by_type.message_received > 0 && (
                        <button
                          onClick={() => { navigate('/cockpit/messages'); setIsOpen(false); }}
                          className="text-xs px-2.5 py-1 bg-white rounded-full border border-gray-200 text-[#1E3A5F] hover:border-[#1E3A5F] transition-colors"
                        >
                          Voir les messages
                        </button>
                      )}
                      {(notificationSummary.by_type.quote_accepted > 0 || notificationSummary.by_type.quote_refused > 0) && (
                        <button
                          onClick={() => { navigate('/cockpit/devis'); setIsOpen(false); }}
                          className="text-xs px-2.5 py-1 bg-white rounded-full border border-gray-200 text-[#1E3A5F] hover:border-[#1E3A5F] transition-colors"
                        >
                          Voir les devis
                        </button>
                      )}
                      {notificationSummary.by_type.appointment_reminder > 0 && (
                        <button
                          onClick={() => { navigate('/cockpit/agenda'); setIsOpen(false); }}
                          className="text-xs px-2.5 py-1 bg-white rounded-full border border-gray-200 text-[#1E3A5F] hover:border-[#1E3A5F] transition-colors"
                        >
                          Voir l'agenda
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* Boutons d'accès rapide aux fonctionnalités personnelles - 8 boutons en grille 4x2 */}
            <div className="grid grid-cols-4 gap-1 mb-3">
              {personalActions.map((action, i) => {
                const Icon = action.icon;
                return (
                  <button
                    key={i}
                    onClick={() => handlePersonalAction(action)}
                    className="flex flex-col items-center gap-1 p-2 rounded-lg bg-white border border-gray-100 hover:border-[#1E3A5F]/30 hover:shadow-sm transition-all group"
                  >
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center transition-transform group-hover:scale-105"
                      style={{ backgroundColor: `${action.color}15` }}
                    >
                      <Icon size={16} style={{ color: action.color }} />
                    </div>
                    <span className="text-[9px] font-medium text-gray-600 leading-tight">{action.label}</span>
                  </button>
                );
              })}
            </div>
            
            {/* Bouton pour voir les Superpouvoirs */}
            <button
              onClick={() => setShowSuperpouvoirsSection(!showSuperpouvoirsSection)}
              className="w-full flex items-center justify-center gap-2 py-2 mb-3 rounded-lg bg-gradient-to-r from-[#1E3A5F]/5 to-[#3B82F6]/5 border border-[#1E3A5F]/10 hover:border-[#1E3A5F]/30 transition-all group"
            >
              <Zap className="w-4 h-4 text-[#3B82F6]" />
              <span className="text-xs font-medium text-[#1E3A5F]">
                {showSuperpouvoirsSection ? 'Masquer' : 'Voir'} les Superpouvoirs
              </span>
              <Lock className="w-3 h-3 text-gray-400" />
              <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${showSuperpouvoirsSection ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Section Superpouvoirs avec cadenas */}
            <AnimatePresence>
              {showSuperpouvoirsSection && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="bg-gradient-to-br from-[#1E3A5F]/5 to-[#3B82F6]/5 rounded-xl p-3 mb-3 border border-[#1E3A5F]/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-[#3B82F6]" />
                      <span className="text-xs font-semibold text-[#1E3A5F]">Superpouvoirs</span>
                      <span className="text-[9px] bg-[#F97316] text-white px-1.5 py-0.5 rounded-full">Bientôt</span>
                    </div>
                    <div className="grid grid-cols-4 gap-1">
                      {(isProMode ? superpouvoirs_pro : superpouvoirs).map((sp, i) => {
                        const Icon = sp.icon;
                        return (
                          <button
                            key={i}
                            onClick={() => handleSuperpouvoirClick(sp)}
                            className="flex flex-col items-center gap-1 p-2 rounded-lg bg-white/60 border border-gray-100 hover:border-[#3B82F6]/30 hover:shadow-sm transition-all group relative"
                          >
                            {/* Icône cadenas */}
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-gray-500 rounded-full flex items-center justify-center shadow-sm">
                              <Lock className="w-2.5 h-2.5 text-white" />
                            </div>
                            <div 
                              className="w-8 h-8 rounded-lg flex items-center justify-center transition-transform group-hover:scale-105 opacity-70"
                              style={{ backgroundColor: `${sp.color}15` }}
                            >
                              <Icon size={16} style={{ color: sp.color }} />
                            </div>
                            <span className="text-[8px] font-medium text-gray-500 leading-tight text-center line-clamp-2">{sp.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[9px] text-gray-400 mt-2 text-center">
                      Clique sur un superpouvoir pour être prévenu de sa disponibilité
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Séparateur avec texte */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-[10px] text-gray-400 font-medium">OU DISCUTE AVEC MOI</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            
            <p className="text-[11px] text-gray-400">
              Pose-moi une question, je peux t'aider à naviguer, trouver des artisans, gérer tes projets...
            </p>
          </div>
        ) : (
          messages.map((msg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                msg.role === 'user' 
                  ? 'bg-[#F97316] text-white' 
                  : 'bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] text-white'
              }`}>
                {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
              </div>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                msg.role === 'user'
                  ? 'bg-[#F97316] text-white rounded-tr-md'
                  : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-tl-md'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                {msg.actions?.some(a => a.type === 'navigate') && (
                  <button
                    onClick={() => handleAction(msg.actions.find(a => a.type === 'navigate'))}
                    className="mt-2 flex items-center gap-1 text-xs text-[#1E3A5F] font-medium hover:underline"
                  >
                    Aller à la page <ArrowRight className="w-3 h-3" />
                  </button>
                )}
                {msg.role === 'assistant' && (
                  <button
                    onClick={() => speakText(msg.content)}
                    className={`mt-1 transition-colors ${isSpeaking ? 'text-[#F97316]' : 'text-gray-400 hover:text-[#1E3A5F]'}`}
                  >
                    <Volume2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </motion.div>
          ))
        )}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex gap-3"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="bg-white rounded-2xl rounded-tl-md px-4 py-3 shadow-sm border border-gray-100">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-[#1E3A5F] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-[#1E3A5F] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-[#1E3A5F] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </motion.div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 bg-white border-t border-gray-100">
        {/* Hidden file inputs */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleCameraCapture}
        />
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageSelect}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv"
          className="hidden"
          onChange={handleFileSelect}
        />

        {/* Mode Pro indicator */}
        {isProMode && (
          <div className="flex items-center gap-2 mb-2 px-2">
            <span className="text-xs bg-[#1E3A5F] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
              <Briefcase className="w-3 h-3" /> Mode Pro
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 relative">
          {/* Bouton + avec menu */}
          <div className="relative">
            <button
              onClick={() => setShowPlusMenu(!showPlusMenu)}
              disabled={isLoading}
              className={`p-2.5 rounded-full transition-all ${
                showPlusMenu 
                  ? 'bg-[#1E3A5F] text-white rotate-45' 
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              title="Plus d'options"
              data-testid="ai-plus-menu-btn"
            >
              <Plus className="w-5 h-5 transition-transform" />
            </button>

            {/* Menu popup */}
            <AnimatePresence>
              {showPlusMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute bottom-14 left-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden min-w-[180px] z-10"
                  data-testid="ai-plus-menu"
                >
                  {/* Bouton Social Posting - En premier et mis en avant */}
                  <button
                    onClick={() => {
                      setShowPlusMenu(false);
                      if (isAuthenticated()) {
                        setShowSocialPostingFlow(true);
                      } else {
                        setLoginPopupAction('créer un post');
                        setShowLoginPopup(true);
                      }
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#F97316]/10 to-[#3B82F6]/10 hover:from-[#F97316]/20 hover:to-[#3B82F6]/20 transition-colors text-left border-b border-gray-100"
                    data-testid="ai-menu-social-post"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#F97316] to-[#3B82F6] flex items-center justify-center">
                      <Share2 className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-800 block">Créer un post</span>
                      <span className="text-[10px] text-gray-500">Photo/Vidéo → Texte IA</span>
                    </div>
                  </button>
                  <button
                    onClick={() => cameraInputRef.current?.click()}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                    data-testid="ai-menu-camera"
                  >
                    <Camera className="w-5 h-5 text-[#1E3A5F]" />
                    <span className="text-sm text-gray-700">Caméra</span>
                  </button>
                  <button
                    onClick={() => imageInputRef.current?.click()}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                    data-testid="ai-menu-photo"
                  >
                    <Image className="w-5 h-5 text-[#F97316]" />
                    <span className="text-sm text-gray-700">Photo</span>
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                    data-testid="ai-menu-file"
                  >
                    <FileText className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-gray-700">Fichier</span>
                  </button>
                  <div className="border-t border-gray-100" />
                  <button
                    onClick={toggleProMode}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left ${isProMode ? 'bg-[#1E3A5F]/5' : ''}`}
                    data-testid="ai-menu-pro"
                  >
                    <Briefcase className={`w-5 h-5 ${isProMode ? 'text-[#1E3A5F]' : 'text-gray-400'}`} />
                    <span className={`text-sm ${isProMode ? 'text-[#1E3A5F] font-medium' : 'text-gray-700'}`}>
                      Mode Pro {isProMode && '✓'}
                    </span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Bouton micro */}
          <button
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isLoading}
            className={`p-2.5 rounded-full transition-colors ${
              isRecording 
                ? 'bg-red-500 text-white animate-pulse' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            title={isRecording ? 'Arrêter' : 'Parler'}
          >
            {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>

          {/* Input texte */}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isProMode ? "Mode Pro — Question technique..." : "Pose-moi une question..."}
            disabled={isLoading || isRecording}
            className="flex-1 px-4 py-2.5 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 disabled:opacity-50"
            data-testid="ai-chat-input"
          />

          {/* Bouton envoyer */}
          <button
            onClick={() => sendMessage(inputValue)}
            disabled={isLoading || !inputValue.trim()}
            className="p-2.5 rounded-full bg-[#F97316] text-white hover:bg-[#ea6c0a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="ai-chat-send"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </motion.div>
  );

  // Ne pas afficher si caché par un drawer
  if (isHiddenByDrawer) return null;
  
  // Ne pas afficher en mode Réseau
  if (isReseauMode) return null;
  
  // Ne pas afficher sur la page Assistant full-page (évite le chevauchement avec le panneau Artifacts)
  if (isAssistantPage) return null;
  
  // Ne pas afficher dans les Cockpits Pro/Particulier (l'assistant IA est déjà intégré dedans)
  if (isCockpitMode) return null;

  return (
    <>
      {/* Floating Button IA - Style Meta avec expansion "Active tes superpouvoirs" */}
      <motion.div
        data-testid="ai-assistant-fab-container"
        className="fixed flex items-center max-sm:right-3 sm:right-4"
        style={{ 
          bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
          zIndex: isOpen ? 9990 : 99999,  // Derrière la fenêtre chat quand ouverte
          pointerEvents: isOpen ? 'none' : 'auto'  // Désactive les clics quand ouvert
        }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: isOpen ? 0 : 1, scale: isOpen ? 0.8 : 1 }}
        transition={{ delay: isOpen ? 0 : 0.5, type: 'spring' }}
      >
        {/* Texte expansible "Active tes superpouvoirs" */}
        <AnimatePresence>
          {isExpanded && !isOpen && (
            <motion.div
              initial={{ width: 0, opacity: 0, x: 20 }}
              animate={{ width: 'auto', opacity: 1, x: 0 }}
              exit={{ width: 0, opacity: 0, x: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="overflow-hidden mr-2 hidden sm:block"
            >
              <div 
                className="px-4 py-2.5 rounded-full whitespace-nowrap text-sm font-semibold text-white"
                style={{
                  background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 50%, #3B82F6 100%)',
                  boxShadow: '0 4px 15px rgba(30, 58, 95, 0.3)',
                }}
              >
                Active tes superpouvoirs
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bouton rond avec baguette magique - ORANGE */}
        <motion.button
          className="flex items-center justify-center"
          data-testid="ai-assistant-fab"
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: 'radial-gradient(circle at center, #F97316 0%, #EA580C 40%, #C2410C 100%)',
            boxShadow: '0 4px 20px rgba(249, 115, 22, 0.4), 0 0 30px rgba(249, 115, 22, 0.2)',
          }}
          whileHover={{ scale: 1.05, boxShadow: '0 6px 25px rgba(249, 115, 22, 0.5), 0 0 40px rgba(249, 115, 22, 0.3)' }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            // Sur desktop (largeur > 768px), rediriger vers la page complète
            if (window.innerWidth >= 768) {
              // Rediriger vers la bonne page selon le contexte
              if (location.pathname.startsWith('/cockpit-pro')) {
                navigate('/cockpit-pro/assistant');
              } else if (location.pathname.startsWith('/cockpit-particulier')) {
                navigate('/cockpit-particulier/assistant');
              } else {
                navigate('/assistant');
              }
            } else {
              // Sur mobile, garder le comportement actuel
              setIsOpen(!isOpen);
            }
          }}
          animate={{ 
            boxShadow: [
              '0 4px 20px rgba(249, 115, 22, 0.4), 0 0 30px rgba(249, 115, 22, 0.2)',
              '0 4px 25px rgba(249, 115, 22, 0.5), 0 0 40px rgba(249, 115, 22, 0.35)',
              '0 4px 20px rgba(249, 115, 22, 0.4), 0 0 30px rgba(249, 115, 22, 0.2)',
            ]
          }}
          transition={{ 
            boxShadow: {
              repeat: Infinity,
              duration: 3,
              ease: 'easeInOut'
            }
          }}
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
                <ChevronDown className="w-6 h-6 text-white" />
              </motion.div>
            ) : (
              <motion.div 
                key="wand" 
                initial={{ rotate: 90, opacity: 0 }} 
                animate={{ rotate: 0, opacity: 1 }} 
                exit={{ rotate: -90, opacity: 0 }} 
                className="relative"
              >
                {/* Baguette magique */}
                <Wand2 className="w-6 h-6 text-white" />
                {/* Petites étoiles autour quand expandé */}
                {isExpanded && (
                  <>
                    <motion.span
                      className="absolute -top-1 -right-1 text-yellow-300"
                      animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                    >
                      ✦
                    </motion.span>
                    <motion.span
                      className="absolute -bottom-0.5 -left-1 text-yellow-300 text-xs"
                      animate={{ scale: [1, 1.2, 1], opacity: [0.7, 1, 0.7] }}
                      transition={{ repeat: Infinity, duration: 0.8, delay: 0.3 }}
                    >
                      ✦
                    </motion.span>
                  </>
                )}
                {/* Badge de notifications */}
                {notificationCount > 0 && !isExpanded && (
                  <motion.span 
                    className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full border-2 border-white text-white text-[10px] font-bold flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                  >
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </motion.span>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </motion.div>

      {/* Chat Window or Voice Mode */}
      <AnimatePresence>
        {isOpen && (voiceOnlyMode ? renderVoiceOnlyMode() : renderChatMode())}
      </AnimatePresence>
      
      {/* Popup de connexion */}
      <RegisterPromptModal 
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
        actionLabel={loginPopupAction}
      />
      
      {/* Modal Superpouvoirs Waitlist */}
      <SuperpouvoirsWaitlistModal
        isOpen={showSuperpouvoirModal}
        onClose={() => {
          setShowSuperpouvoirModal(false);
          setSelectedSuperpouvoir(null);
        }}
        feature={selectedSuperpouvoir}
        onSubscribe={(feature) => {
          // Ajouter un message dans le chat
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ Super ! Tu es inscrit pour "${feature.label}". Je te préviendrai dès que c'est disponible !`,
            timestamp: new Date().toISOString()
          }]);
        }}
      />
      
      {/* Popup Mode Pro - Disponible le 1er mai */}
      <AnimatePresence>
        {showModeProPopup && (
          <motion.div
            className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModeProPopup(false)}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header gradient */}
              <div className="bg-gradient-to-r from-[#1E3A5F] to-[#E8650A] p-6 text-white text-center">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center">
                  <Briefcase className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-1">Mode Pro</h3>
                <p className="text-white/80 text-sm">Bientôt disponible</p>
              </div>
              
              {/* Contenu */}
              <div className="p-6 text-center">
                <div className="mb-4">
                  <div className="text-4xl font-bold text-[#1E3A5F] mb-1">1er Mai</div>
                  <div className="text-gray-500 text-sm">2026</div>
                </div>
                
                <p className="text-gray-600 text-sm mb-4">
                  Le Mode Pro de ton assistant arrive bientôt avec des fonctionnalités dédiées aux professionnels : CRM, devis, factures, gestion d'équipe...
                </p>
                
                <div className="bg-[#1E3A5F]/5 rounded-lg p-3 mb-4">
                  <p className="text-xs text-[#1E3A5F] font-medium">
                    🚀 Offre Fondateur disponible jusqu'au 1er avril !
                  </p>
                </div>
                
                <button
                  onClick={() => setShowModeProPopup(false)}
                  className="w-full py-2.5 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] text-white rounded-lg font-medium hover:shadow-lg transition-all"
                >
                  J'ai compris !
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modal Social Posting Flow */}
      <AnimatePresence>
        {showSocialPostingFlow && (
          <motion.div
            className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSocialPostingFlow(false)}
          >
            <div onClick={e => e.stopPropagation()}>
              <SocialPostingFlow
                userId={localStorage.getItem('userId')}
                onClose={() => setShowSocialPostingFlow(false)}
                onComplete={() => {
                  // Ajouter un message de confirmation dans le chat
                  setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: '🎉 Ton post a été publié avec succès ! Tu peux le retrouver dans le fil correspondant.',
                    timestamp: new Date().toISOString()
                  }]);
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingAIAssistant;
